"use client";

import { c as _c } from "react/compiler-runtime";
import { trackEmailComposerAction } from "../analytics";
import { ColorControl } from "../block-properties-panel";
import { type StyleMap, getStyleMap, readStyle, writeStyle } from "../blocks/shared-block-styles";
import { useEmailComposer } from "../context";
import { type Editor, useEditorState } from "@tiptap/react";
import { useState } from "react";
import { LuImage, LuPaintBucket, LuTrash2 } from "react-icons/lu";
import { Button } from "@/components/ui";
import { Label } from "@/components/ui/label";
import { cx } from "@/lib/tailwind-utils";
import { toast } from "@/lib/use-toast";
import { ImageUrlForm } from "./image-url-form";
import { SECTION_PADDING_OPTIONS, backgroundImageStyle, pickImageFiles, readBackgroundImage } from "./layout-presets";

/**
 * Keep focus in the editor while a panel button is pressed, like the toolbar's
 * own buttons do. A button that disappears once clicked ("Remove image") would
 * otherwise take focus with it, and the toolbar closes on the lost focus.
 */
const keepEditorFocus = (e: React.MouseEvent) => e.preventDefault();
const BACKGROUND_IMAGE_KEYS = Object.keys(backgroundImageStyle("").style);

/** Position of the `index`th cell of the row at `rowPos`. */
function cellPosition(editor: Editor, rowPos: number, index: number) {
  const row = editor.state.doc.nodeAt(rowPos);
  if (!row || index >= row.childCount) return null;
  let pos = rowPos + 1;
  for (let i = 0; i < index; i++) pos += row.child(i).nodeSize;
  return pos;
}

/**
 * Background colour, background image and padding for one section (cell).
 *
 * Lives in the block toolbar so it works on every screen the composer is on,
 * not just the ones with a properties sidebar. It expands inside the toolbar
 * rather than floating off it: the toolbar moves with every edit, and a popover
 * anchored to it would be left behind.
 */
export function SectionStylePanel({
  rowPos,
  cellIndex
}: {
  rowPos: number;
  cellIndex: number;
}) {
  const {
    editor,
    onImageUpload
  } = useEmailComposer();
  const [uploading, setUploading] = useState(false);
  const attrs = useEditorState({
    editor,
    selector: ctx => {
      if (!ctx.editor) return null;
      const pos = cellPosition(ctx.editor, rowPos, cellIndex);
      return pos === null ? null : ctx.editor.state.doc.nodeAt(pos)?.attrs ?? null;
    }
  });
  if (!editor || !attrs) return null;
  const update = (patch: Record<string, unknown>, action: string) => {
    const pos_0 = cellPosition(editor, rowPos, cellIndex);
    const cell = pos_0 === null ? null : editor.state.doc.nodeAt(pos_0);
    if (pos_0 === null || !cell) return;
    editor.view.dispatch(editor.state.tr.setNodeMarkup(pos_0, undefined, {
      ...cell.attrs,
      ...patch
    }));
    trackEmailComposerAction(action, "block_bubble_menu");
  };
  const backgroundImage = readBackgroundImage(attrs);
  const padding = readStyle(attrs, "padding");
  const attributes = (attrs.attributes ?? {}) as Record<string, string>;
  const withoutBackgroundImage = (): {
    style: StyleMap;
    attributes: Record<string, string>;
  } => {
    const style = {
      ...getStyleMap(attrs)
    };
    for (const key of BACKGROUND_IMAGE_KEYS) delete style[key];
    const rest = {
      ...attributes
    };
    delete rest.background;
    return {
      style,
      attributes: rest
    };
  };
  const setBackgroundImage = (src: string) => {
    const base = withoutBackgroundImage();
    const image = backgroundImageStyle(src);
    update({
      style: {
        ...base.style,
        ...image.style
      },
      attributes: {
        ...base.attributes,
        ...image.attributes
      }
    }, "section_background_image_set");
  };
  const chooseBackgroundImage = async () => {
    if (!onImageUpload) return;
    const [file] = await pickImageFiles();
    if (!file) return;
    setUploading(true);
    try {
      setBackgroundImage(await onImageUpload(file));
    } catch {
      toast({
        title: "Failed to upload image",
        variant: "error"
      });
    } finally {
      setUploading(false);
    }
  };
  return <div className="w-64 space-y-3 border-t border-border p-3">
      <ColorControl label="Background color" value={attrs.backgroundColor as string || ""} onChange={v => update({
      backgroundColor: v
    }, "section_background_color_set")} onClear={() => update({
      backgroundColor: ""
    }, "section_background_color_set")} />

      {onImageUpload && <div className="space-y-1">
          <Label className="text-caption">Background image</Label>
          {backgroundImage && <div className="h-16 w-full rounded-md border border-border bg-cover bg-center" style={{
        backgroundImage: `url('${backgroundImage}')`
      }} />}
          <div className="flex gap-1.5">
            <Button type="button" variant="primary" size="sm" className="flex-1" disabled={uploading} onMouseDown={keepEditorFocus} onClick={chooseBackgroundImage}>
              <LuImage size={14} />
              <span>
                {uploading ? "Uploading…" : backgroundImage ? "Replace image" : "Add image"}
              </span>
            </Button>
            {backgroundImage && <Button type="button" variant="ghost" size="icon-sm" tooltip="Remove image" aria-label="Remove image" onMouseDown={keepEditorFocus} onClick={() => update(withoutBackgroundImage(), "section_background_image_removed")} className="text-status-error-text hover:bg-status-error-bg">
                <LuTrash2 size={14} />
              </Button>}
          </div>
          <ImageUrlForm onSubmit={setBackgroundImage} />
        </div>}

      <div className="space-y-1">
        <Label className="text-caption">Padding</Label>
        <div className="flex gap-1">
          {SECTION_PADDING_OPTIONS.map(option => <Button key={option.label} type="button" variant={padding === option.value ? "secondary" : "primary"} size="sm" className="flex-1" onMouseDown={keepEditorFocus} onClick={() => update(writeStyle(attrs, "padding", option.value, {
          target: "named"
        }), "section_padding_set")}>
              {option.label}
            </Button>)}
        </div>
      </div>
    </div>;
}

/** The toolbar button that opens and closes the section panel. */
export function SectionStyleToggle(t0) {
  const $ = _c(7);
  const {
    open,
    onToggle
  } = t0;
  const t1 = open && "bg-hover";
  let t2;
  if ($[0] !== t1) {
    t2 = cx(t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <LuPaintBucket size={15} />;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== onToggle || $[4] !== open || $[5] !== t2) {
    t4 = <Button type="button" variant="ghost" size="icon-sm" onMouseDown={keepEditorFocus} onClick={onToggle} className={t2} tooltip="Section style" aria-label="Section style" aria-expanded={open}>{t3}</Button>;
    $[3] = onToggle;
    $[4] = open;
    $[5] = t2;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  return t4;
}